import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { usePrompt } from '@/contexts/PromptContext'

interface FormatStepProps {
  value?: any
  onChange?: (value: any) => void
  readOnly?: boolean
}

// Define the format options as simple JSON structure
const FORMAT_OPTIONS = {
  'any_format': false,
  'full_sentences_paragraphs': false,
  'bullet_points': false,
  'table': false,
  'list': false,
  'long_article': false,
  'short_summary': false,
  'spreadsheet_csv_google_sheets': false,
  'powerpoint_google_slides': false,
  'ms_word_google_doc': false,
  'audio_file': false,
  'video_file': false,
  'image_graphic_file': false
}

const FORMAT_KEYS = Object.keys(FORMAT_OPTIONS) as Array<keyof typeof FORMAT_OPTIONS>

const FormatStep: React.FC<FormatStepProps> = ({
  value: externalValue,
  onChange: externalOnChange,
  readOnly = false
}) => {
  const { t } = useTranslation('steps')
  const [selectedFormat, setSelectedFormat] = useState('any_format')
  const { promptData, updatePrompt, loading } = usePrompt()

  // Load existing format data when promptData is available
  useEffect(() => {
    if (promptData?.format && !externalValue) {
      // Find which format is selected and update local state
      const selectedKey = Object.keys(promptData.format).find(key => promptData.format[key] === true)
      if (selectedKey && selectedKey in FORMAT_OPTIONS) {
        setSelectedFormat(selectedKey)
      }
    }
  }, [promptData?.format, externalValue])

  // Update format immediately without debouncing
  const updateFormat = async (newFormatData: any) => {
    try {
      await updatePrompt({ format: newFormatData })
    } catch (error) {
      console.error('Error updating format:', error)
    }
  }

  const handleFormatSelect = (format: string) => {
    if (readOnly) return // Don't allow changes in read-only mode
    
    // Update local state
    setSelectedFormat(format)

    // Create new format data with only the selected format marked as true
    const newFormatData = { ...FORMAT_OPTIONS }
    if (format in FORMAT_OPTIONS) {
      newFormatData[format as keyof typeof FORMAT_OPTIONS] = true
    }
    
    // If we're in refine mode (external onChange), call the external handler
    if (externalOnChange) {
      externalOnChange(newFormatData)
      return
    }
    
    // Update database immediately
    updateFormat(newFormatData)
  }

  return (
    <div className="text-center">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 mt-2 sm:mt-6 px-4">
        {t('format.title')}
      </h2>

      <p className="text-gray-600 mb-6 sm:mb-8 max-w-lg mx-auto px-4 text-xs sm:text-base">
        {t('format.description')}
      </p>

      <div className="max-w-md mx-auto mb-6 sm:mb-8 px-4">
        <Select
          value={selectedFormat}
          onValueChange={handleFormatSelect}
          disabled={readOnly || loading}
        >
          <SelectTrigger className="w-full h-10 sm:h-12 text-sm sm:text-base">
            <SelectValue placeholder={t('format.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {FORMAT_KEYS.map((formatKey) => (
              <SelectItem key={formatKey} value={formatKey} className="text-sm sm:text-base">
                {t(`format.options.${formatKey}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

export default FormatStep
